import type {
  ProblemStatementRating,
  ProblemStatementRollup,
  PsRuleHumanReview,
} from "@/services/api/case-checking";
import { applyRuleOverridesToRollup } from "@/components/case-checking/humanRuleReviewCascade";

export type RuleIssueStatus = "passed" | "failed" | "unclear" | "na";

export type RuleIssueFilter = "all" | "failed" | "unclear";

export interface RuleIssueRow {
  ruleId: string;
  label: string;
  status: RuleIssueStatus;
  agentStatus: RuleIssueStatus;
  humanChanged: boolean;
  evidence: string;
  statementId: string;
  statementTitle: string;
  statementRating: ProblemStatementRating;
}

export function normalizeRuleStatus(status: string): RuleIssueStatus {
  const s = (status || "").toLowerCase();
  if (s === "passed" || s === "pass") return "passed";
  if (s === "failed" || s === "fail") return "failed";
  if (s === "na" || s === "n/a") return "na";
  return "unclear";
}

export function isAttentionRuleStatus(status: RuleIssueStatus): boolean {
  return status === "failed" || status === "unclear";
}

const STATUS_SORT_ORDER: Record<RuleIssueStatus, number> = {
  failed: 0,
  unclear: 1,
  passed: 2,
  na: 3,
};

/** Flat list of failed + unclear rules across all scorable statements (uses human-effective status). */
export function collectRuleIssues(
  rollups: ProblemStatementRollup[],
  overrides?: Map<string, PsRuleHumanReview>,
): RuleIssueRow[] {
  const overrideMap = overrides ?? new Map();
  const rows: RuleIssueRow[] = [];
  for (const rollup of rollups) {
    const { rollup: effectiveRollup, effectiveRules } = applyRuleOverridesToRollup(rollup, overrideMap);
    for (const rule of effectiveRules) {
      const status = rule.effectiveStatus;
      if (!isAttentionRuleStatus(status)) continue;
      rows.push({
        ruleId: rule.rule_id,
        label: rule.label || rule.rule_id,
        status,
        agentStatus: rule.agentStatus,
        humanChanged: rule.humanChanged,
        evidence: rule.evidence,
        statementId: rollup.id,
        statementTitle: rollup.title,
        statementRating: effectiveRollup.rating,
      });
    }
  }
  rows.sort((a, b) => {
    const byStatus = STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];
    if (byStatus !== 0) return byStatus;
    const byStatement = a.statementId.localeCompare(b.statementId);
    if (byStatement !== 0) return byStatement;
    return a.ruleId.localeCompare(b.ruleId);
  });
  return rows;
}

export function countRuleIssues(rows: RuleIssueRow[]): { failed: number; unclear: number } {
  const counts = { failed: 0, unclear: 0 };
  for (const row of rows) {
    if (row.status === "failed") counts.failed += 1;
    else if (row.status === "unclear") counts.unclear += 1;
  }
  return counts;
}

export function filterRuleIssues(rows: RuleIssueRow[], filter: RuleIssueFilter): RuleIssueRow[] {
  if (filter === "all") return rows;
  return rows.filter((row) => row.status === filter);
}

export type RuleHumanCall = "pass" | "fail" | "unclear" | "na" | "excluded";

export function agentStatusToHumanCall(status: string): RuleHumanCall {
  const normalized = normalizeRuleStatus(status);
  if (normalized === "passed") return "pass";
  if (normalized === "failed") return "fail";
  if (normalized === "na") return "na";
  return "unclear";
}

export function isPassedAgentRule(status: string): boolean {
  return normalizeRuleStatus(status) === "passed";
}
